import { useEffect, useState, type RefObject } from "react";

/**
 * Altezza residua fra un elemento e il fondo della finestra.
 *
 * Serve a far crescere gli editor fino a occupare lo spazio disponibile invece
 * di fermarsi a un numero fisso di righe, lasciando mezza pagina vuota.
 *
 * La misura viene presa invece di essere calcolata in CSS perché la posizione
 * dell'editor cambia con il contenuto sopra di esso: la console di un deploy in
 * corso, la descrizione di uno stack, un avviso. Una formula fissa sarebbe
 * sbagliata in tutti i casi tranne uno.
 */

/** Spazio lasciato sotto, perché l'editor non tocchi il bordo della finestra. */
const RESPIRO = 24;

/**
 * Quanto della finestra è già preso da qualcun altro, in basso.
 *
 * La console di un'operazione è ancorata al fondo e la dichiara in
 * `--spazio-console` (vedi components/DeployConsole.tsx): senza leggerla,
 * l'editor arriverebbe fino al bordo della finestra e finirebbe sotto di essa.
 */
const spazioOccupatoInBasso = (): number =>
  parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue("--spazio-console"),
  ) || 0;

export interface OpzioniAltezza {
  /** Sotto questa altezza l'editor resta comunque utilizzabile. */
  readonly minima?: number;
  /** Da sommare quando sotto l'editor ci sono altri elementi. */
  readonly riservato?: number;
}

export function useAltezzaDisponibile(
  riferimento: RefObject<HTMLElement | null>,
  { minima = 260, riservato = 0 }: OpzioniAltezza = {},
): number {
  const [altezza, setAltezza] = useState(minima);

  useEffect(() => {
    const elemento = riferimento.current;
    if (!elemento) return;

    const misura = () => {
      // La distanza si misura dall'inizio del documento, non dal bordo alto
      // della finestra: quella nella finestra cala mentre si scorre, e l'editor
      // cresceva di quanto si era scorso — allungando la pagina, che permetteva
      // di scorrere ancora. Bastavano quattro giri di rotella per raddoppiare
      // l'altezza del documento senza mai arrivare in fondo.
      const distanzaDallInizio = elemento.getBoundingClientRect().top + window.scrollY;
      const disponibile =
        window.innerHeight - distanzaDallInizio - RESPIRO - riservato - spazioOccupatoInBasso();

      setAltezza(Math.max(minima, Math.round(disponibile)));
    };

    misura();

    // La posizione cambia anche senza che la finestra cambi dimensione: basta
    // che compaia un avviso sopra l'editor.
    // Rimandata al fotogramma successivo: misurare dentro la notifica
    // dell'osservatore incatena due giri di layout nello stesso ciclo, che il
    // browser segnala come «ResizeObserver loop».
    let prossimoFotogramma = 0;
    const osservatore = new ResizeObserver(() => {
      cancelAnimationFrame(prossimoFotogramma);
      prossimoFotogramma = requestAnimationFrame(misura);
    });
    osservatore.observe(document.body);
    window.addEventListener("resize", misura);

    return () => {
      cancelAnimationFrame(prossimoFotogramma);
      osservatore.disconnect();
      window.removeEventListener("resize", misura);
    };
  }, [riferimento, minima, riservato]);

  return altezza;
}
